import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import { getApiBaseUrl, ADD_ACTIVITY, LOGIN } from "../../../Config/Config";
import { Select } from "antd";

const FilterTreeAddAreaModel = ({
  show,
  onClose,
  clickedBlock,
  // selectedBlock,
  onSuccess,
}) => {
  const [areaNameInput, setAreaNameInput] = useState(""); // this is Area ID
  const [areas, setAreas] = useState([]); // full list with Id + Name

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchAreas();
  }, []);

  const fetchAreas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/Areas/getareas`);
      const json = await res.json();

      if (json?.Status) {
        setAreas(json.ResultData || []);
      } else {
        console.error("Failed to fetch areas:", json?.Message);
      }
    } catch (err) {
      console.error("Error fetching areas:", err);
    } finally {
      setLoading(false);
    }
  };

  // Helper: Get Area Name from ID
  const getAreaNameById = (areaId) => {
    const area = areas.find((a) => a.Id === Number(areaId));
    return area ? area.Name : "Unknown Area";
  };

  const handleSave = async () => {
    const selectedAreaName = getAreaNameById(areaNameInput);

    const logActivity = async (isSuccess, notes) => {
      try {
        await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ActivityType: 1,
            PerformedBy: user?.UserID,
            PerformedOn: new Date()
              .toISOString()
              .slice(0, 19)
              .replace("T", " "),
            Notes: notes,
            Location: "assignArea",
            IsSucces: isSuccess ? 1 : 0,
          }),
        });
      } catch (err) {
        console.error("Failed to log activity:", err);
      }
    };

    const payload = {
      BlockId: clickedBlock?.blockId,
      AreaId: Number(areaNameInput),
      CreatedBy: user?.UserID,
    };

    try {
      setLoading(true);

      const response = await fetch(`${getApiBaseUrl()}/Areas/addblockareas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      const status = result?.ResultData?.[0];

      if (response.ok && status?.Status === "Success") {
        await logActivity(
          true,
          `Area "${selectedAreaName}" assigned successfully to  Block "${clickedBlock?.blockName}"`
        );

        Swal.fire("Success", "Area assigned to Block successfully!", "success").then(() => {
          onSuccess(); // reload parent
          onClose();
        });
      } else {
        await logActivity(
          false,
          `Failed to assign Block "${clickedBlock?.blockName}" to Area "${selectedAreaName}"`
        );
        Swal.fire("Failed", status?.ResultMessage || "Something went wrong!", "error");
      }
    } catch (error) {
      console.error("Network error:", error);
      await logActivity(
        false,
        `Error assigning Block to Area "${selectedAreaName}": ${error.message}`
      );
      Swal.fire("Error", "Unable to connect to server.", "error");
    } finally {
      setLoading(false);
      setAskPassword(false);
      setPassword("");
      setError("");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!areaNameInput) {
      setError("Please select an Area.");
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please select an Area.",
      });
      return;
    }

    if (!user?.UserID) {
      setError("Invalid user data. Please log in again.");
      Swal.fire({
        icon: "error",
        title: "User Data Error",
        text: "Invalid user data. Please log in again.",
      });
      return;
    }

    setAskPassword(true);
  };

  const handleAuth = async () => {
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setAuthLoading(true);
    setError("");

    try {
      if (!user?.UserName) {
        throw new Error("User name not found in session data.");
      }

      const payload = {
        username: user.UserName,
        password,
      };

      const res = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status) {
        setAskPassword(false);
        setPassword("");
        await handleSave();
      } else {
        throw new Error(data.Message || "Invalid credentials. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Unable to authenticate. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", zIndex: 1055 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Assign Area</h5>
              <button
                type="button"
                onClick={onClose}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "22px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  cursor: "pointer",
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="row">
                {/* Block (Disabled) */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Block</label>
                  <input
                    className="form-control"
                    value={clickedBlock?.blockName || "—"}
                    disabled
                  />
                </div>

                {/* Area Selection */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Area Name <span className="text-danger ms-1">*</span>
                  </label>
                  <Select
                    showSearch
                    placeholder="Select Area"
                    value={areaNameInput || undefined}
                    style={{ width: "100%", height: 40 }}
                    getPopupContainer={(triggerNode) => triggerNode.parentElement}
                    optionFilterProp="children"
                    onChange={(value) => setAreaNameInput(value)}
                    filterOption={(input, option) =>
                      option?.children?.toLowerCase().includes(input.toLowerCase())
                    }
                    disabled={askPassword}
                  >
                    {areas.map((item) => (
                      <Select.Option key={item.Id} value={item.Id}>
                        {item.Name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>

              {askPassword && (
                <div className="mt-2">
                  <div className="mb-3 w-lg-75 col-4">
                    <label className="form-label">
                      Confirm Password <span className="text-danger"> *</span>
                    </label>
                    <div className="pass-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className="pass-input form-control"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setError("");
                        }}
                        onCopy={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                        onCut={(e) => e.preventDefault()}
                        placeholder="Enter Password"
                      />
                      <span
                        className={`ti toggle-password ${
                          showPassword ? "ti-eye" : "ti-eye-off"
                        }`}
                        onClick={() => setShowPassword(!showPassword)}
                      ></span>
                    </div>
                  </div>

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  <button
                    className="btn btn-primary btn-sm me-2"
                    onClick={handleAuth}
                    disabled={authLoading || !password}
                  >
                    {authLoading ? "Verifying..." : "Confirm"}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setAskPassword(false);
                      setPassword("");
                      setError("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {!askPassword && (
              <div className="modal-footer gap-2">
                <button className="btn btn-secondary" onClick={onClose}>
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={onClose}
      />
    </>
  );
};

FilterTreeAddAreaModel.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clickedBlock: PropTypes.object,
  selectedBlock: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default FilterTreeAddAreaModel;